import { type DefinedInitialDataInfiniteOptions, type InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Media, MediaView, PageableContent, SafetyOccurrence, SafetyReviewHistory, SafetyReviewRequest, UUID } from '@cobliteam/common-types';

import { CameraSafetyRequester } from '@cobliteam/api-lib';

import { MediaTableType, getMediaStatusBasedOnReviewStatus } from '@cobliteam/media';
import Environment from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const cameraSafetyRequester = new CameraSafetyRequester({
  host: env.apiHost,
});

const ROOT_KEY = 'CAMERA-SAFETY';
const OCCURRENCES_KEY = [ROOT_KEY, 'OCCURRENCES'];
const REVIEW_HISTORY_KEY = [ROOT_KEY, 'REVIEW-HISTORY'];

const useCameraSafety = () => {
  const getCameraSafetyOccurrences = () => {
    return useQuery<SafetyOccurrence[]>({
      queryKey: OCCURRENCES_KEY,
      queryFn: cameraSafetyRequester.getCameraSafetyOccurrences,
      staleTime: 300_000, // 5 minutes
    });
  };

  const upsertCameraSafetyReview = (page: MediaTableType) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (safetyReviewRequest: SafetyReviewRequest) => cameraSafetyRequester.upsertCameraSafetyOccurrences(safetyReviewRequest),
      onSuccess: async (result, { mediaId, driverName, updatedByName }) => {
        const queryKeyPage = page === MediaTableType.EVENT_MEDIA ? 'LIST-EVENT' : 'LIST-REQUEST';

        if (mediaId) {
          // Invalidate cached history
          await queryClient.invalidateQueries({
            queryKey: [...REVIEW_HISTORY_KEY, mediaId],
          });

          const previousList = await queryClient.getQueriesData({
            queryKey: ['CAMERA-MEDIA', queryKeyPage],
          });

          const previousView = await queryClient.getQueriesData({
            queryKey: ['CAMERA-MEDIA', 'VIEW', mediaId],
          });

          queryClient.setQueriesData(
            {
              queryKey: ['CAMERA-MEDIA', queryKeyPage],
            },
            (old: InfiniteData<PageableContent<Media>> | undefined) => {
              if (!old) return old;
              const updatedPages = old.pages.map((item) => {
                return {
                  ...item,
                  content: item.content.map((media: Media) => {
                    if (media.id === mediaId) {
                      return {
                        ...media,
                        mediaStatus: getMediaStatusBasedOnReviewStatus(result.status),
                        driverName: driverName || media.driver?.name,
                        safetyReview: {
                          ...media.safetyReview,
                          id: result.id,
                          fleetId: result.fleetId,
                          createdAt: result.createdAt,
                          updatedAt: result.updatedAt,
                          updatedByUser: {
                            fullName: updatedByName,
                          },
                          createdBy: result.createdBy,
                          updatedBy: result.updatedBy,
                          status: result.status,
                          driverId: result.driverId,
                          driver: {
                            name: driverName || '',
                          },
                          occurrences:
                            result.occurrences?.map((occurrence) => {
                              return {
                                ...occurrence,
                                id: occurrence.id,
                                name: occurrence.name,
                              };
                            }) || [],
                        },
                      };
                    }
                    return media;
                  }),
                };
              });
              return {
                ...old,
                pages: updatedPages,
              };
            }
          );

          queryClient.setQueriesData(
            {
              queryKey: ['CAMERA-MEDIA', 'VIEW', mediaId],
            },
            (old: MediaView | undefined) => {
              if (!old) return old;
              return {
                ...old,
                mediaInfo: {
                  ...old.mediaInfo,
                  mediaStatus: getMediaStatusBasedOnReviewStatus(result.status),
                  safetyReview: {
                    ...old.mediaInfo.safetyReview,
                    id: result.id,
                    fleetId: result.fleetId,
                    createdAt: result.createdAt,
                    updatedAt: result.updatedAt,
                    createdBy: result.createdBy,
                    updatedByUser: {
                      fullName: updatedByName,
                    },
                    updatedBy: result.updatedBy,
                    status: result.status,
                    driverId: result.driverId,
                    driver: {
                      name: driverName || '',
                    },
                    occurrences:
                      result.occurrences?.map((occurrence) => {
                        return {
                          ...occurrence,
                          id: occurrence.id,
                          name: occurrence.name,
                        };
                      }) || [],
                  },
                },
              };
            }
          );

          return { previousList, previousView };
        }
      },
    });
  };

  const getSafetyReviewHistory = (mediaId: UUID, options?: Partial<DefinedInitialDataInfiniteOptions<PageableContent<SafetyReviewHistory>>>) => {
    return useInfiniteQuery<PageableContent<SafetyReviewHistory>>({
      queryKey: [...REVIEW_HISTORY_KEY, mediaId],
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        cameraSafetyRequester.getSafetyReviewHistory({
          mediaId,
          page: pageParam as number,
          size: 10,
          sort: ['createdAt,desc'],
        }),
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.last === false) return pages.length;
        return undefined;
      },
      staleTime: 60_000,
      ...options,
    });
  };

  return {
    getCameraSafetyOccurrences,
    upsertCameraSafetyReview,
    getSafetyReviewHistory,
  };
};

export default useCameraSafety;
